import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
} from "react";
import validators from "./validations";
import { FaMinus, FaPlus } from "react-icons/fa";

// Helper to run all validators for a field
function runValidators(value, validations = [], label = "") {
  for (const rule of validations || []) {
    if (typeof rule === "string" && validators[rule]) {
      const err = validators[rule](value, label);
      if (err) return err;
    }
    if (typeof rule === "function") {
      const err = rule(value, label);
      if (err) return err;
    }
  }
  return null;
}

const DynamicForm = forwardRef(
  ({ fields, initialData, onSubmit, editMode, onChange }, ref) => {
    const [formData, setFormData] = useState(initialData || {});
    const [errors, setErrors] = useState({});
    const [selectFilters, setSelectFilters] = useState({});
    const [selectOpen, setSelectOpen] = useState({});
    const selectRefs = useRef({});

    useEffect(() => {
      const handleClickOutside = (event) => {
        setSelectOpen((prev) => {
          if (!prev || Object.keys(prev).length === 0) return prev;

          let hasChanges = false;
          const next = { ...prev };

          Object.keys(prev).forEach((fieldName) => {
            if (!prev[fieldName]) return;
            const fieldRef = selectRefs.current[fieldName];
            if (fieldRef && !fieldRef.contains(event.target)) {
              next[fieldName] = false;
              hasChanges = true;
            }
          });

          return hasChanges ? next : prev;
        });
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Update single field
    const handleChange = (name, value) => {
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        onChange && onChange(updated);
        return updated;
      });
    };

    // Update subform field
    const handleSubChange = (fieldName, index, subName, value) => {
      setFormData((prev) => {
        const updatedRows = [...(prev[fieldName] || [])];
        updatedRows[index][subName] = value;
        const updated = { ...prev, [fieldName]: updatedRows };
        onChange && onChange(updated);
        return updated;
      });
    };

    // Add new row to subform
    const handleAddRow = (fieldName, subFields) => {
      setFormData((prev) => ({
        ...prev,
        [fieldName]: [
          ...(prev[fieldName] || []),
          Object.fromEntries(subFields.map((f) => [f.name, ""])),
        ],
      }));
    };

    // Remove row from subform
    const handleRemoveRow = (fieldName, index) => {
      const updatedRows = [...(formData[fieldName] || [])];
      updatedRows.splice(index, 1);
      setFormData((prev) => ({ ...prev, [fieldName]: updatedRows }));
    };
    useEffect(() => {
      setFormData(initialData);
      onChange && onChange(initialData);

      // // Initialize selectFilters for searchable selects with their selected option labels
      // const initialFilters = {};
      // fields.forEach((field) => {
      //   if (field.type === "select" && field.searchable && initialData[field.name]) {
      //     const selectedOption = (field.options || []).find((opt) => opt.value === initialData[field.name]);
      //     if (selectedOption) {
      //       initialFilters[field.name] = selectedOption.label;
      //     }
      //   }
      // });
      // setSelectFilters(initialFilters);
    }, []);

    // Update form data and select filters when initialData changes
    useEffect(() => {
      if (initialData && Object.keys(initialData).length > 0) {
        setFormData(initialData);
        setErrors({}); // Clear any previous validation errors

        // Update selectFilters for searchable selects
        const updatedFilters = { ...selectFilters };
        fields.forEach((field) => {
          if (field.type === "select" && field.searchable && initialData[field.name]) {
            const selectedOption = (field.options || []).find((opt) => opt.value == initialData[field.name]);
            if (selectedOption) {
              updatedFilters[field.name] = selectedOption.label;
            }
          }
        });
        setSelectFilters(updatedFilters);

        // Don't call onChange here to avoid circular updates
        // onChange && onChange(initialData);
      }
    }, [initialData]);

    // Update select filters when fields change (e.g., when options are loaded asynchronously)
    useEffect(() => {
      const updatedFilters = { ...selectFilters };
      let hasChanges = false;
      fields.forEach((field) => {
        if (field.type === "select" && field.searchable && formData[field.name] && !updatedFilters[field.name]) {
          const selectedOption = (field.options || []).find((opt) => opt.value == formData[field.name]);
          if (selectedOption) {
            updatedFilters[field.name] = selectedOption.label;
            hasChanges = true;
          }
        }
      });
      if (hasChanges) {
        setSelectFilters(updatedFilters);
      }
    }, [fields, formData]);

    // Validation
    const validate = () => {
      const newErrors = {};
      fields.forEach((field) => {
        if (field.type === "subform") {
          (formData[field.name] || []).forEach((row, rowIndex) => {
            field.subFields.forEach((sf) => {
              const err = runValidators(row[sf.name], sf.validations, sf.label);
              if (err) {
                if (!newErrors[field.name]) newErrors[field.name] = [];
                if (!newErrors[field.name][rowIndex])
                  newErrors[field.name][rowIndex] = {};
                newErrors[field.name][rowIndex][sf.name] = err;
              }
            });
          });
        } else {
          const err = runValidators(
            formData[field.name],
            field.validations,
            field.label
          );
          if (err) newErrors[field.name] = err;
        }
      });
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    useImperativeHandle(ref, () => ({
      validate,
      getData: () => formData,
    }));

    // --- RENDER FIELDS: 3 per row, subform full row ---
    const rows = [];
    let currentRow = [];

    fields?.forEach((field, idx) => {
      // Helper to check if a field is required
      const isRequired = (validations) =>
        Array.isArray(validations) && validations.includes("required");

      if (field.type === "subform") {
        if (currentRow.length) {
          rows.push(
            <div className="row g-3" key={`row-${rows.length}`}>
              {currentRow}
            </div>
          );
          currentRow = [];
        }
        rows.push(
          <div key={field.name} className="mb-3">
            {(formData[field.name] || []).map((row, rowIndex) => (
              <div key={rowIndex} className="row g-3 align-items-center mb-2">
                {field.subFields.map((sf) => (
                  <div key={sf.name} className="col">
                    <label className="form-label fw-bold">
                      {sf.label}
                      {isRequired(sf.validations) && (
                        <span style={{ color: "red" }}> *</span>
                      )}
                    </label>
                    <input
                      type={sf.type}
                      className="form-control"
                      placeholder={sf.label}
                      value={row[sf.name] || ""}
                      onChange={(e) =>
                        handleSubChange(
                          field.name,
                          rowIndex,
                          sf.name,
                          e.target.value
                        )
                      }
                      disabled={sf.disabled}
                      title={sf.label}
                    />

                    <h6>
                      <span style={{ display: "hidden" }}>&nbsp;</span>
                      <span className="text-danger small fw-normal">
                        {errors[field.name]?.[rowIndex]?.[sf.name] &&
                          errors[field.name][rowIndex][sf.name]}
                      </span>
                    </h6>
                  </div>
                ))}
                {editMode !== "customer" && (
                  <div className="col-auto d-flex align-items-center">
                    <span
                      style={{
                        marginLeft: 8,
                        cursor: "pointer",
                        color: "#003087",
                        fontSize: 23,
                      }}
                      onClick={() => handleAddRow(field.name, field.subFields)}
                      title="Add Another Row"
                    >
                      <FaPlus />
                    </span>
                    <span
                      style={{
                        marginLeft: 8,
                        cursor:
                          (formData[field.name] || []).length > 1
                            ? "pointer"
                            : "not-allowed",
                        color:
                          (formData[field.name] || []).length > 1
                            ? "#003087"
                            : "#ccc",
                        fontSize: 22,
                      }}
                      onClick={() =>
                        (formData[field.name] || []).length > 1 &&
                        handleRemoveRow(field.name, rowIndex)
                      }
                      title="Remove Row"
                    >
                      <FaMinus />
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      } else {
          currentRow.push(
          <div key={field.name} className="col-md-4 mb-1">
            <label className="form-label fw-bold">
              {field.label}
              {isRequired(field.validations) && (
                <span style={{ color: "red" }}> *</span>
              )}
            </label>
              {field.type === "select" ? (
                field.searchable ? (
                  // Single combo input: type to filter options and pick one from dropdown
                  <div
                    className="position-relative"
                    ref={(el) => {
                      if (el) selectRefs.current[field.name] = el;
                    }}
                  >
                    <input
                      type="text"
                      className="form-control"
                      placeholder={field.placeholder || field.searchPlaceholder || `Select ${field.label}`}
                      title={field.label}
                      value={
                        // show typed filter if present,
                        // otherwise show selected option label,
                        // or if the field is disabled and has a single Loading... option show that label
                        (selectFilters[field.name] !== undefined && selectFilters[field.name] !== null)
                          ? selectFilters[field.name]
                          : ((field.options || []).find((o) => o.value === formData[field.name])?.label) || (field.disabled && (field.options && field.options[0]?.label) ? field.options[0].label : "")
                      }
                      onChange={(e) => {
                        const v = e.target.value;
                        handleChange(field.name, "");
                        setSelectFilters((prev) => ({ ...prev, [field.name]: v }));
                        setSelectOpen((prev) => ({ ...prev, [field.name]: true }));
                      }}
                      onFocus={() => setSelectOpen((prev) => ({ ...prev, [field.name]: true }))}
                      onBlur={() => {
                        // delay closing to allow click on option; also auto-select exact match
                        setTimeout(() => {
                          const typed = (selectFilters[field.name] || "").toString().trim();
                          if (typed) {
                            const match = (field.options || []).find(
                              (o) => (o.label || "").toLowerCase() === typed.toLowerCase()
                            );
                            if (match) {
                              handleChange(field.name, match.value);
                              setSelectFilters((prev) => ({ ...prev, [field.name]: match.label }));
                            }
                          }
                          setSelectOpen((prev) => ({ ...prev, [field.name]: false }));
                        }, 150);
                      }}
                      disabled={field.disabled || false}
                    />
                    {selectOpen[field.name] && (
                      <div
                        className="dropdown-menu show"
                        style={{ width: "100%", maxHeight: 240, overflowY: "auto" }}
                      >
                        {(field.options || [])
                          .filter((opt) => {
                            const filter = (selectFilters[field.name] || "").toLowerCase();
                            if (!filter) return true;
                            return (opt.label || "").toLowerCase().includes(filter);
                          })
                          .map((opt) => (
                            <button
                              type="button"
                              key={opt.value}
                              className="dropdown-item"
                              onMouseDown={(e) => {
                                // prevent blur from hiding before click
                                e.preventDefault();
                                handleChange(field.name, opt.value);
                                setSelectFilters((prev) => ({ ...prev, [field.name]: opt.label }));
                                setSelectOpen((prev) => ({ ...prev, [field.name]: false }));
                              }}
                            >
                              {opt.label}
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="position-relative"
                    ref={(el) => {
                      if (el) selectRefs.current[field.name] = el;
                    }}
                  >
                    <div
                      className="form-select d-flex align-items-center justify-content-between"
                      style={{
                        cursor: field.disabled ? "not-allowed" : "pointer",
                        backgroundColor: field.disabled ? "#e9ecef" : "",
                      }}
                      onClick={() => {
                        if (!field.disabled) {
                          setSelectOpen((prev) => ({
                            ...prev,
                            [field.name]: !prev[field.name],
                          }));
                        }
                      }}
                      aria-disabled={field.disabled || false}
                      title={field.label}
                    >
                      <span>
                        {(field.options || []).find(
                          (opt) => opt.value == formData[field.name]
                        )?.label || field.placeholder || `Select ${field.label}`}
                      </span>
                    </div>
                    {selectOpen[field.name] && !field.disabled && (
                      <div
                        className="dropdown-menu show position-absolute w-100"
                        style={{ zIndex: 1000, maxHeight: 240, overflowY: "auto" }}
                      >
                        <button
                          type="button"
                          className="dropdown-item"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleChange(field.name, "");
                            setSelectOpen((prev) => ({ ...prev, [field.name]: false }));
                          }}
                        >
                          {field.placeholder || `Select ${field.label}`}
                        </button>
                        {(field.options || []).map((opt) => (
                          <button
                            type="button"
                            key={opt.value}
                            className="dropdown-item"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleChange(field.name, opt.value);
                              setSelectOpen((prev) => ({
                                ...prev,
                                [field.name]: false,
                              }));
                            }}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )
              ) : field.type === "multiselect" ? (
                // Multi-select dropdown with checkboxes
                <div
                  className="position-relative"
                  ref={(el) => {
                    if (el) selectRefs.current[field.name] = el;
                  }}
                >
                  <div
                    className="form-control d-flex align-items-center"
                    style={{ cursor: "pointer", minHeight: "38px" }}
                    onClick={() => setSelectOpen((prev) => ({ ...prev, [field.name]: !prev[field.name] }))}
                    title={field.label}
                  >
                    <div className="flex-grow-1">
                      {Array.isArray(formData[field.name]) && formData[field.name].length > 0
                        ? field.options
                            .filter((opt) => formData[field.name].includes(opt.value))
                            .map((opt) => opt.label)
                            .join(", ")
                        : field.placeholder || `Select ${field.label}`}
                    </div>
                    <span className="ms-2">▼</span>
                  </div>
                  {selectOpen[field.name] && (
                    <div
                      className="dropdown-menu show position-absolute w-100"
                      style={{ zIndex: 1000, maxHeight: 240, overflowY: "auto" }}
                    >
                      {field.options.map((opt) => (
                        <div key={opt.value} className="dropdown-item p-2">
                          <label className="form-check-label d-flex align-items-center">
                            <input
                              type="checkbox"
                              className="form-check-input me-2"
                              checked={Array.isArray(formData[field.name]) && formData[field.name].includes(opt.value)}
                              onChange={(e) => {
                                const currentValues = Array.isArray(formData[field.name]) ? formData[field.name] : [];
                                let newValues;
                                if (e.target.checked) {
                                  newValues = [...currentValues, opt.value];
                                } else {
                                  newValues = currentValues.filter((v) => v !== opt.value);
                                }
                                handleChange(field.name, newValues);
                              }}
                            />
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
              <input
                type={field.type}
                className="form-control"
                value={formData[field.name] || ""}
                onChange={(e) => handleChange(field.name, e.target.value)}
                disabled={field.disabled || false}
                placeholder={field.placeholder || field.label}
                style={field.style}
                max={field.max}
                title={field.label}
              />
            )}
            <h6>
              <span style={{ display: "hidden" }}>&nbsp;</span>
              <span className="text-danger small fw-normal">
                {errors[field.name] && errors[field.name]}{" "}
              </span>
            </h6>
          </div>
        );
      }
    });

    if (currentRow.length) {
      rows.push(
        <div className="row gy-0 gx-3" key={`row-${rows.length}`}>
          {currentRow}
        </div>
      );
    }

    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (validate()) onSubmit(formData);
        }}
      >
        {rows}
      </form>
    );
  }
);

export default DynamicForm;
